import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * React ErrorBoundary that catches runtime errors in child components
 * and displays a fallback UI instead of crashing the entire app.
 *
 * Without this, any unhandled error in a component causes React to
 * unmount the entire tree, which looks like the UI "flashing then disappearing."
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });
    // Log to console for debugging
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleHardReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const errorMessage = this.state.error?.message || 'Unknown error';
      const componentStack = this.state.errorInfo?.componentStack || '';

      return (
        <div className="min-h-screen bg-surface-950 flex items-center justify-center p-6">
          <div className="max-w-lg w-full bg-surface-900 border border-surface-700 rounded-xl p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-loss/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-loss" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-surface-100">Something went wrong</h2>
            </div>

            <p className="text-sm text-surface-400">
              The application encountered an unexpected error. This has been captured to prevent
              the entire interface from disappearing. You can try recovering or reload the page.
            </p>

            <div className="bg-surface-950 rounded-lg p-3 border border-surface-800">
              <p className="text-xs text-loss font-mono break-words">{errorMessage}</p>
            </div>

            {componentStack && (
              <details className="text-xs text-surface-600">
                <summary className="cursor-pointer hover:text-surface-400 transition-colors">
                  Component Stack (click to expand)
                </summary>
                <pre className="mt-2 p-2 bg-surface-950 rounded overflow-x-auto text-surface-500 whitespace-pre-wrap">
                  {componentStack}
                </pre>
              </details>
            )}

            <div className="flex gap-3">
              <button
                onClick={this.handleReload}
                className="px-4 py-2 text-sm font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
              >
                Try Recovery
              </button>
              <button
                onClick={this.handleHardReload}
                className="px-4 py-2 text-sm font-medium bg-surface-700 text-surface-200 rounded-lg hover:bg-surface-600 transition-colors"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Lightweight inline error boundary for wrapping individual components
 * or sections. Shows a minimal error message instead of crashing the whole page.
 */
export class SectionErrorBoundary extends React.Component<
  { children: React.ReactNode; name?: string },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode; name?: string }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    console.error(`[SectionErrorBoundary${this.props.name ? ` (${this.props.name})` : ''}]`, error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 bg-loss/10 border border-loss/20 rounded-lg">
          <p className="text-sm text-loss">
            ⚠️ This section failed to render{this.props.name ? ` (${this.props.name})` : ''}.
          </p>
          <p className="text-xs text-surface-500 mt-1">
            {this.state.error?.message || 'Unknown error'}
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="mt-2 text-xs text-brand-400 hover:text-brand-300"
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
